import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Box, Button, TextField, Typography, Grid2, Stack, Container,
  Checkbox, FormControlLabel, Collapse, Avatar, Card, CardContent, Divider, Paper, IconButton, Fade, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { AccessTime, Person, DateRange, Remove, KeyboardArrowDown } from '@mui/icons-material';
import { Appointment, Employee, BookingConfirmation, CustomerCreateInput, AppointmentStatus, ScheduleCacheUpdateInput } from '../../types';
import { Timestamp } from 'firebase/firestore';
import {
  addToEmployeeAppointments, createAppointment, addBookingToSchedule,
  createCustomer, deleteAppointment, getAppointmentById, getImageFromStorage
} from '../../firebase/firebase-delegate';
import { functions } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import { formatDate, formatTime, formatTimeRange } from '../../time/timeUtils';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useRecaptcha } from '../../utils/ReCaptchaContext';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { WEBSITE_ID } from '../../projectConfig';
import { getCachedAssetUrl } from '../../utils/assetCache';

interface ConfirmAppointmentProps {
  employee: Employee;
  appointment: Appointment;
  businessName: string;
  businessConfig?: {
    preferences?: {
      orchestratorMode?: boolean;
      primaryOrchestratorId?: string;
    };
    paymentInfo?: {
      owner?: string;
    };
  } | null;
  allEmployees?: Employee[];
  handleAppointment: (appointment: Appointment | null) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  handleErrorDialogOpen: (errorMessage: string, options?: { refreshSchedule?: boolean }) => void;
  handleScheduleCacheUpdate: (input: ScheduleCacheUpdateInput) => void;
  handleBookingConfirmation: (bookingDetails: BookingConfirmation, attemptedCardPayment: boolean, paid: boolean) => void;
  enabledCustomerSMS: boolean;
  enabledEmployeeSMS: boolean;
  goToPreviousStep: () => void;
  canTakeCard: boolean;
  enabledPayments: boolean;
  requirePaymentForBooking: boolean;
  allowTips: boolean;
  clearAppointmentOnSuccess?: boolean;
  resolveAnyAvailableConflict?: () => Promise<Employee | null>;
  prefillCustomer?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}

interface VerifyCaptchaResponse { success: boolean; }
interface VerifyCaptchaRequest { token: string; }
interface CreateCustomerPaymentResponse {
  paid?: boolean;
  error?: string | null;
  paymentIntentId?: string | null;
  amountCents?: number | null;
  serviceAmountCents?: number | null;
  tipAmountCents?: number | null;
}

const SLOT_CONFLICT_ERROR_CODE = 'SLOT_CONFLICT';

type SlotConflictError = Error & { code: typeof SLOT_CONFLICT_ERROR_CODE };

const createSlotConflictError = (): SlotConflictError => {
  const error = new Error('Looks like that time is now unavailable. Please pick another available slot.') as SlotConflictError;
  error.code = SLOT_CONFLICT_ERROR_CODE;
  return error;
};

const isSlotConflictError = (error: unknown): error is SlotConflictError => {
  return error instanceof Error && (error as SlotConflictError).code === SLOT_CONFLICT_ERROR_CODE;
};

const ensureValidOrchestratorNotificationTarget = (
  businessConfig: ConfirmAppointmentProps['businessConfig'],
  allEmployees: Employee[],
) => {
  if (businessConfig?.preferences?.orchestratorMode !== true) {
    return;
  }

  const primaryOrchestratorId = businessConfig.preferences?.primaryOrchestratorId?.trim() || '';
  const ownerId = businessConfig.paymentInfo?.owner?.trim() || '';
  const fallbackTargetId = ownerId || '';
  const targetIdToValidate = primaryOrchestratorId || fallbackTargetId;

  if (!targetIdToValidate) {
    throw new Error('Booking is temporarily unavailable: orchestrator notifications are not configured.');
  }

  const targetEmployeeExists = allEmployees.some((candidate) => candidate.id === targetIdToValidate);
  if (!targetEmployeeExists) {
    // Guardrail: in orchestrator mode we should not accept bookings when employee notifications
    // cannot be routed to a valid orchestrator/owner target.
    throw new Error('Booking is temporarily unavailable: orchestrator notification target is invalid.');
  }
};

export const validateForm = (trimmedFirstName: string, trimmedLastName: string, trimmedEmail: string, trimmedPhone: string) => {
  const newErrors: { firstName?: string; lastName?: string; email?: string; phone?: string; } = {};
  const namePattern = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/;

  if (!trimmedFirstName || !namePattern.test(trimmedFirstName)) {
    newErrors.firstName = 'Please enter a valid first name with only text.';
  }

  if (!trimmedLastName || !namePattern.test(trimmedLastName)) {
    newErrors.lastName = "Please enter a valid last name with only text.";
  }

  if (!trimmedEmail || !/\S+@\S+\.\S+/.test(trimmedEmail)) {
    newErrors.email = 'Please enter a valid email address.';
  }

  if (!isValidPhoneNumber(trimmedPhone || '')) {
    newErrors.phone = 'Please enter a valid phone number';
  }

  return newErrors;
};

const ConfirmAppointment: React.FC<ConfirmAppointmentProps> = ({
  employee,
  appointment,
  businessName,
  businessConfig = null,
  allEmployees = [],
  handleAppointment,
  setIsSubmitting,
  handleErrorDialogOpen,
  handleScheduleCacheUpdate,
  handleBookingConfirmation,
  enabledCustomerSMS,
  enabledEmployeeSMS,
  goToPreviousStep,
  canTakeCard,
  enabledPayments,
  requirePaymentForBooking,
  allowTips,
  clearAppointmentOnSuccess = true,
  resolveAnyAvailableConflict,
  prefillCustomer,
}) => {
  const [firstName, setFirstName] = useState(prefillCustomer?.firstName ?? '');
  const [lastName, setLastName] = useState(prefillCustomer?.lastName ?? '');
  const [email, setEmail] = useState(prefillCustomer?.email ?? '');
  const [phone, setPhone] = useState(prefillCustomer?.phone ?? '');
  const [sendCustomerSMS, setSendCustomerSMS] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>(appointment.notes || '');
  const [selectedTipOption, setSelectedTipOption] = useState<'0' | '15' | '20' | '25' | 'custom'>('0');
  const [customTipInput, setCustomTipInput] = useState<string>('');
  const [employeePhotoUrl, setEmployeePhotoUrl] = useState<string | null>(null);

  const [cardError, setCardError] = useState<string | null>(null);

  // Error messages
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; email?: string; phone?: string; captcha?: string }>({});

  const stripe = useStripe();
  const elements = useElements();
  const recaptchaRef = useRecaptcha();

  // Resolve "can we show card UI?"
  const allowCard: boolean = (enabledPayments && canTakeCard);
  const mustPayNow: boolean = (requirePaymentForBooking);
  // Show card section immediately if required & allowed; otherwise hidden until "Pay now" is clicked
  const [showCardSection, setShowCardSection] = useState<boolean>(mustPayNow && allowCard);
  const customTipValue = Number(customTipInput);
  const isCustomTipInvalid =
    selectedTipOption === 'custom' &&
    customTipInput.trim().length > 0 &&
    (!Number.isFinite(customTipValue) || customTipValue < 0);
  const isCustomTipTooHigh =
    selectedTipOption === 'custom' &&
    Number.isFinite(customTipValue) &&
    customTipValue > 1000;
  const isTipInputInvalid = isCustomTipInvalid || isCustomTipTooHigh;
  const tipAmountCents = (() => {
    if (!allowTips || !allowCard || !showCardSection) return 0;
    if (selectedTipOption === 'custom') {
      if (!Number.isFinite(customTipValue) || customTipValue <= 0) return 0;
      return Math.round(customTipValue * 100);
    }
    const percentage = Number(selectedTipOption);
    if (percentage <= 0) return 0;
    return Math.round((appointment.billingAmount * percentage / 100) * 100);
  })();
  const tipAmount = tipAmountCents / 100;
  const shouldApplyTip = allowTips && allowCard && showCardSection;
  const totalWithTip = appointment.billingAmount + (shouldApplyTip ? tipAmount : 0);
  const formatUsd = (amount: number) => `$${amount.toFixed(2)}`;
  const tip15Amount = appointment.billingAmount * 0.15;
  const tip20Amount = appointment.billingAmount * 0.20;
  const tip25Amount = appointment.billingAmount * 0.25;
  const customTipDisplayAmount = Number.isFinite(customTipValue) && customTipValue > 0 ? customTipValue : 0;

  const [summaryCollapsed, setSummaryCollapsed] = useState<boolean>(false);
  const collapseTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (mustPayNow && allowCard) setShowCardSection(true);
  }, [mustPayNow, allowCard]);
  useEffect(() => {
    if (!showCardSection) {
      setSelectedTipOption('0');
      setCustomTipInput('');
    }
  }, [showCardSection]);
  useEffect(() => {
    setNotes(appointment.notes || '');
  }, [appointment.notes]);
  useEffect(() => {
    if (!prefillCustomer) return;
    setFirstName((current) => current || prefillCustomer.firstName);
    setLastName((current) => current || prefillCustomer.lastName);
    setEmail((current) => current || prefillCustomer.email);
    setPhone((current) => current || prefillCustomer.phone);
  }, [prefillCustomer]);

  const maybeCollapseSummary = () => {
    if (!summaryCollapsed) setSummaryCollapsed(true);
  };
  const scheduleCollapse = () => {
    if (summaryCollapsed) return;
    if (collapseTimerRef.current) window.clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = window.setTimeout(() => {
      setSummaryCollapsed(true);
      collapseTimerRef.current = null;
    }, 500);
  };
  const cancelScheduledCollapse = () => {
    if (collapseTimerRef.current) {
      window.clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  };
  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) window.clearTimeout(collapseTimerRef.current);
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadEmployeePhoto = async () => {
      const profilePath = employee.profilePictureImagePath;
      if (!profilePath) {
        setEmployeePhotoUrl(null);
        return;
      }

      try {
        const url = await getCachedAssetUrl(
          profilePath,
          () => getImageFromStorage(profilePath)
        );
        if (isMounted) {
          setEmployeePhotoUrl(url);
        }
      } catch (error) {
        console.error('Failed to load employee profile picture', error);
        if (isMounted) {
          setEmployeePhotoUrl(null);
        }
      }
    };

    // Non-critical UI side effect: do not block booking flow on avatar preload.
    loadEmployeePhoto();
    return () => {
      isMounted = false;
    };
  }, [employee.profilePictureImagePath]);

  const formatCompactDateTime = () => {
    const [month, day] = [appointment.appointmentDate.slice(0, 2), appointment.appointmentDate.slice(2, 4)];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabel = months[parseInt(month, 10) - 1];
    const dayLabel = parseInt(day, 10);
    return `${monthLabel} ${dayLabel} at ${formatTime(appointment.appointmentTime).toLowerCase()}`;
  };

  // === Mutations ===
  const addBookingToScheduleMutation = useMutation({
    mutationFn: async ({ employeeId, date, selectedTime, appointmentDuration }:
      { employeeId: string, date: string, selectedTime: string, appointmentDuration: number }) => {
      const appointmentSuccess = await addBookingToSchedule(
        employeeId,
        date,
        selectedTime,
        appointmentDuration,
        false,
        undefined,
        WEBSITE_ID,
        { throwOnOperationalError: true },
      );
      if (!appointmentSuccess) {
        throw createSlotConflictError();
      }
    },
  });

  const createAppointmentMutation = useMutation({
    mutationFn: async (tempAppointmentWithoutId: Omit<Appointment, 'id'>) => {
      return await createAppointment(tempAppointmentWithoutId);
    }
  });

  // Add the specific appointment ID to employee appointments map
  const addToEmployeeAppointmentsMutation = useMutation({
    mutationFn: async ({ employeeId, appointmentId, date }:
      { employeeId: string, appointmentId: string, date: string }) => {

      const employeeAppointmentSuccess = await addToEmployeeAppointments(employeeId, appointmentId, date);

      if (!employeeAppointmentSuccess) {
        throw new Error("Failed to update employee appointment map");
      }
    }
  });

  // Create customer document
  const createCustomerMutation = useMutation({
    mutationFn: async (customerData: CustomerCreateInput) => { await createCustomer(customerData); }
  });

  const handleBookingErrorRollback = async ({
    didUpdateSchedule,
    scheduledEmployeeId,
    scheduledAppointmentDate,
    scheduledAppointmentTime,
    scheduledAppointmentDuration,
    appointmentId,
    tempCompleteAppointment,
    paid,
    requirePaymentForBooking,
  }: {
    didUpdateSchedule: boolean;
    scheduledEmployeeId: string;
    scheduledAppointmentDate: string;
    scheduledAppointmentTime: string;
    scheduledAppointmentDuration: number;
    appointmentId: string | null;
    tempCompleteAppointment: Appointment | null;
    paid: boolean;
    requirePaymentForBooking: boolean;
  }) => {
    try {
      console.log('[Booking] Rollback check', { didUpdateSchedule, appointmentId, paid, requirePaymentForBooking });
      // If payment was required and not paid, and an appointment exists, cancel it
      if (requirePaymentForBooking && !paid && appointmentId && tempCompleteAppointment) {
        try {
          await deleteAppointment(tempCompleteAppointment);
        } catch (cancelErr) {
          // Best-effort rollback cleanup: preserve the original booking failure path.
          console.error('[Booking] Cancel on rollback failed', cancelErr);
        }
        return;
      }
      // If we blocked the time but never created an appointment, free the slot
      if (!appointmentId && !tempCompleteAppointment && didUpdateSchedule) {
        console.log('[Booking] Reverting blocked schedule…');
        const revertOk = await addBookingToSchedule(
          scheduledEmployeeId,
          scheduledAppointmentDate,
          scheduledAppointmentTime,
          scheduledAppointmentDuration,
          true
        );
        if (!revertOk) throw new Error("Error reverting employee schedule");
        console.log('[Booking] Schedule reverted');
      }

      // If it was paid or not required, keep as-is (booked path).
    } catch (error) {
      // Rollback is best-effort cleanup; the original booking error is surfaced separately.
      console.error("Failed to rollback appointment: ", error);
    }
  };


  const handleConfirmAppointment = async () => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = (phone || '').trim();

    const newErrors = validateForm(trimmedFirstName, trimmedLastName, trimmedEmail, trimmedPhone);

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    if (cardError) return;
    if (!recaptchaRef.current) {
      handleErrorDialogOpen("reCAPTCHA failed. Please refresh/try again", { refreshSchedule: true });
      return;
    }

    try {
      recaptchaRef.current.reset();
      const token = await recaptchaRef.current.executeAsync();
      if (!token) throw new Error("CAPTCHA token missing");
      await confirmAppointmentMutation.mutateAsync(token);
    } catch (error: any) {
      if (!error?.rollbackContext) {
        handleErrorDialogOpen("Something went wrong. Please try again", { refreshSchedule: true });
      }
    }
  };

  // On 'Confirm Appointment' button click
  const confirmAppointmentMutation = useMutation({
    mutationFn: async (captchaToken: string) => {
      setIsSubmitting(true);
      // Ensure if its orchestrator mode, that its set right.
      ensureValidOrchestratorNotificationTarget(businessConfig, allEmployees);

      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();
      const trimmedEmail = email.trim();
      const trimmedPhone = phone.trim();
      const billingDetails = {
        name: `${trimmedFirstName} ${trimmedLastName}`.trim() || undefined,
        email: trimmedEmail || undefined,
        phone: trimmedPhone || undefined,
      };

      // === Payment method logic ===
      let paymentMethodId: string | null = null;
      let attemptedPayment = false; // track if we attempted any card flow
      let paid = false;
      let confirmedPaymentInfo: Appointment['paymentInfo'] | undefined;

      if (mustPayNow) {
        if (!allowCard) throw new Error("Card payment is required to book this appointment. Please enter your card details.");
        if (!stripe || !elements) throw new Error("Payment system not ready. Please wait a moment and try again.");
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error("Please enter your card details to continue.");

        // Count as an attempt BEFORE trying to create PM
        attemptedPayment = true;

        const { paymentMethod, error: pmErr } =
          await stripe.createPaymentMethod({ type: 'card', card: cardElement, billing_details: billingDetails });
        if (pmErr || !paymentMethod) throw new Error(pmErr?.message || "Unable to use this card. Please try another payment method.");
        paymentMethodId = paymentMethod.id;
      } else {
        // Optional payment path — only attempt PM creation if the CardElement is present (i.e., user clicked "Pay now")
        if (allowCard && stripe && elements) {
          const cardElement = elements.getElement(CardElement);
          if (cardElement) {
            // User chose to try card: mark attempt now
            attemptedPayment = true;

            const { paymentMethod, error: paymentError } =
              await stripe.createPaymentMethod({ type: 'card', card: cardElement, billing_details: billingDetails });
            if (!paymentError && paymentMethod) {
              paymentMethodId = paymentMethod.id;
            } else if (paymentError) {
              console.warn('[Payment] Optional card entry failed; proceeding unpaid.', paymentError?.message);
              // Optional path continues unpaid; we still record 'attempted'
            }
          }
        }
      }

      // 1) Verify CAPTCHA
      const verifyCaptchaCallable = httpsCallable<VerifyCaptchaRequest, VerifyCaptchaResponse>(functions, "verifyCaptcha");
      const result = await verifyCaptchaCallable({ token: captchaToken });
      if (!result.data.success) throw new Error("CAPTCHA verification failed. Please refresh/try again.");

      // 2) Prepare appointment
      const trimmedNotes = notes.trim().slice(0, 160);
      let bookingEmployee = employee;
      let bookingAppointment: Appointment = {
        ...appointment,
        employeeID: employee.id,
        employeeName: `${employee.firstName} ${employee.lastName}`,
        employeeEmail: employee.email || '',
        employeePhoneNumber: employee.phone,
      };
      const buildTempAppointmentWithoutId = (): Omit<Appointment, 'id'> => ({
        employeeID: bookingEmployee.id,
        employeeName: `${bookingEmployee.firstName} ${bookingEmployee.lastName}`,
        employeeEmail: bookingEmployee.email || '',
        employeePhoneNumber: bookingEmployee.phone,
        billingAmount: appointment.billingAmount,
        creationTime: Timestamp.now(),
        appointmentDate: bookingAppointment.appointmentDate,
        appointmentTime: bookingAppointment.appointmentTime,
        status: mustPayNow ? AppointmentStatus.PENDING_PAYMENT : AppointmentStatus.UPCOMING,
        customerEmail: trimmedEmail,
        customerFirstName: trimmedFirstName,
        customerLastName: trimmedLastName,
        customerPhoneNumber: trimmedPhone,
        appointmentTitle: appointment.appointmentTitle,
        appointmentDuration: appointment.appointmentDuration,
        servicesList: appointment.servicesList,
        businessName: businessName,
        businessUrl: `${window.location.protocol}//${window.location.host}`,
        lastUpdater: '',
        allowSendCustomerSMS: sendCustomerSMS,
        allowSendEmployeeSMS: enabledEmployeeSMS,
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      });

      let didUpdateSchedule = false;
      let appointmentId: string | null = null;
      let tempCompleteAppointment: Appointment | null = null;
      let scheduledEmployeeId = '';
      let scheduledAppointmentDate = '';
      let scheduledAppointmentTime = '';
      let scheduledAppointmentDuration = 0;

      try {
        // ✅ 3) Update schedule
        try {
          await addBookingToScheduleMutation.mutateAsync({
            employeeId: bookingEmployee.id,
            date: bookingAppointment.appointmentDate,
            selectedTime: bookingAppointment.appointmentTime,
            appointmentDuration: bookingAppointment.appointmentDuration,
          });
        } catch (scheduleError) {
          if (!isSlotConflictError(scheduleError)) {
            throw scheduleError;
          }
          const fallbackEmployee = await resolveAnyAvailableConflict?.();
          if (!fallbackEmployee) {
            throw scheduleError;
          }

          bookingEmployee = fallbackEmployee;
          bookingAppointment = {
            ...bookingAppointment,
            employeeID: fallbackEmployee.id,
            employeeName: `${fallbackEmployee.firstName} ${fallbackEmployee.lastName}`,
            employeeEmail: fallbackEmployee.email || '',
            employeePhoneNumber: fallbackEmployee.phone || '',
          };

          await addBookingToScheduleMutation.mutateAsync({
            employeeId: bookingEmployee.id,
            date: bookingAppointment.appointmentDate,
            selectedTime: bookingAppointment.appointmentTime,
            appointmentDuration: bookingAppointment.appointmentDuration,
          });
        }
        didUpdateSchedule = true;
        scheduledEmployeeId = bookingEmployee.id;
        scheduledAppointmentDate = bookingAppointment.appointmentDate;
        scheduledAppointmentTime = bookingAppointment.appointmentTime;
        scheduledAppointmentDuration = bookingAppointment.appointmentDuration;

        // ✅ 4) Create appointment doc
        const tempAppointmentWithoutId = buildTempAppointmentWithoutId();
        appointmentId = await createAppointmentMutation.mutateAsync(tempAppointmentWithoutId);
        tempCompleteAppointment = { ...tempAppointmentWithoutId, id: appointmentId };

        // ✅ 5) Link to employee appointments
        await addToEmployeeAppointmentsMutation.mutateAsync({
          employeeId: bookingEmployee.id,
          appointmentId,
          date: bookingAppointment.appointmentDate,
        });

        // 6) Charge if we have a PM
        if (paymentMethodId) {
          try {
            const createPayment = httpsCallable(functions, 'createCustomerPayment');
            const paymentResult = await createPayment({
              siteId: WEBSITE_ID,
              serviceIds: appointment.servicesList.map((s) => s.id),
              payment_method_id: paymentMethodId,
              tip_amount_cents: shouldApplyTip ? tipAmountCents : 0,
              phone: trimmedPhone,
              appointmentId: appointmentId,
              customerEmail: trimmedEmail,
              customerFirstName: trimmedFirstName,
              customerLastName: trimmedLastName,
            });
            const data = (paymentResult.data as CreateCustomerPaymentResponse) || {};
            paid = Boolean(data.paid);
            if (paid) {
              const serviceAmountCents = typeof data.serviceAmountCents === 'number'
                ? data.serviceAmountCents
                : Math.round(appointment.billingAmount * 100);
              const computedTipAmountCents = shouldApplyTip ? tipAmountCents : 0;
              const tipAmountCentsFromServer = typeof data.tipAmountCents === 'number'
                ? data.tipAmountCents
                : computedTipAmountCents;
              const amountCents = typeof data.amountCents === 'number'
                ? data.amountCents
                : serviceAmountCents + tipAmountCentsFromServer;

              confirmedPaymentInfo = {
                paymentIntentId: data.paymentIntentId ?? undefined,
                serviceAmountCents,
                tipAmountCents: tipAmountCentsFromServer,
                amountCents,
              };

              // Prefer persisted values when available so confirmation matches stored payment records.
              if (appointmentId) {
                try {
                  const savedAppointment = await getAppointmentById(appointmentId, WEBSITE_ID);
                  if (savedAppointment.paymentInfo) {
                    confirmedPaymentInfo = savedAppointment.paymentInfo;
                  }
                } catch (refreshError) {
                  console.warn('[Booking] Failed to refresh appointment payment info for confirmation.', refreshError);
                }
              }
            }

            if (!paid && mustPayNow) throw new Error(data?.error || "Card was declined. No charge made.");
            if (!paid && !mustPayNow) {
              // Non-critical side effect: booking remains successful in optional-card mode.
              handleErrorDialogOpen("Card payment didn't go through. Your appointment is booked — please pay at the place.", { refreshSchedule: true });
            }
          } catch (payErr: any) {
            if (mustPayNow) throw new Error(payErr?.message || "Payment failed. Your card was not charged.");
            else {
              console.log('[Payment] Optional charge failed; proceed unpaid', payErr);
              // Non-critical side effect: booking remains successful in optional-card mode.
              handleErrorDialogOpen("Card payment didn't go through. Your appointment is booked — please pay at the place.", { refreshSchedule: true });
            }
          } finally {
            try { elements?.getElement(CardElement)?.clear(); } catch { }
          }
        } else if (mustPayNow) {
          // Required payment but we never got a PM (attempt already recorded above)
          throw new Error("Card payment is required to book this appointment. Please enter your card details.");
        }

        // ✅ 7) Create/Update customer record (even if payment failed optionally)
        await createCustomerMutation.mutateAsync({
          id: trimmedPhone, firstName: trimmedFirstName, lastName: trimmedLastName,
          email: trimmedEmail, phone: trimmedPhone, employeeIds: [bookingEmployee.id],
        });

        console.log("attemptedCardPayment", attemptedPayment, "paid", paid);

        // ✅ 8) Update local state and confirm
        if (!tempCompleteAppointment || !appointmentId) {
          throw new Error('Appointment not finalized. Please try again.');
        }
        handleBookingConfirmation(
          {
            ...tempCompleteAppointment,
            id: appointmentId,
            employee: `${bookingEmployee.firstName} ${bookingEmployee.lastName}`,
            paid,
            ...(confirmedPaymentInfo ? { paymentInfo: confirmedPaymentInfo } : {}),
          },
          attemptedPayment,
          paid
        );

        handleScheduleCacheUpdate({
          employeeId: bookingEmployee.id,
          appointmentDate: bookingAppointment.appointmentDate,
          appointmentTime: bookingAppointment.appointmentTime,
          appointmentDuration: bookingAppointment.appointmentDuration,
        });

        if (clearAppointmentOnSuccess) {
          handleAppointment(null);
        }

      } catch (error) {
        const wrappedError = new Error((error as Error)?.message || "Booking failed");
        (wrappedError as any).rollbackContext = {
          originalError: error,
          didUpdateSchedule,
          scheduledEmployeeId,
          scheduledAppointmentDate,
          scheduledAppointmentTime,
          scheduledAppointmentDuration,
          appointmentId,
          tempCompleteAppointment,
          paid,
          requirePaymentForBooking: mustPayNow,
        };
        throw wrappedError;
      }
    },
    onError: async (error: any) => {
      console.error("Booking mutation error:", error);
      const rollback = error?.rollbackContext ?? {};
      const original = rollback.originalError ?? error.originalError ?? "Booking failed. Please refresh and try again.";

      await handleBookingErrorRollback({
        didUpdateSchedule: rollback?.didUpdateSchedule ?? false,
        scheduledEmployeeId: rollback?.scheduledEmployeeId ?? '',
        scheduledAppointmentDate: rollback?.scheduledAppointmentDate ?? '',
        scheduledAppointmentTime: rollback?.scheduledAppointmentTime ?? '',
        scheduledAppointmentDuration: rollback?.scheduledAppointmentDuration ?? 0,
        appointmentId: rollback?.appointmentId ?? null,
        tempCompleteAppointment: rollback?.tempCompleteAppointment ?? null,
        paid: rollback?.paid ?? false,
        requirePaymentForBooking: rollback?.requirePaymentForBooking ?? false,
      });

      let message = original instanceof Error ? original.message : String(original);
      if (message.toLowerCase().includes('error updating employee schedule')) {
        message = 'Looks like that time is now unavailable. Please pick another available slot.';
      }
      handleErrorDialogOpen(message, { refreshSchedule: true });
    },
    onSettled: () => {
      setIsSubmitting(false);
    }
  });

  return (
    <Grid2 container direction="column" alignItems="center" sx={{ minHeight: '100%', backgroundColor: 'background.default' }}>
      <Container
        maxWidth="sm"
        disableGutters
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{ flexGrow: 1, px: { xs: 0, sm: 2 } }}
          onScrollCapture={maybeCollapseSummary}
          onWheel={maybeCollapseSummary}
          onTouchMove={maybeCollapseSummary}
        >
          <Box sx={{ height: 8 }} />
          <Card
            elevation={2}
            onClick={summaryCollapsed ? () => setSummaryCollapsed(false) : undefined}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              cursor: summaryCollapsed ? 'pointer' : 'default',
              '&:active': { backgroundColor: 'transparent' },
              '&:focus-visible': { outline: 'none' },
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <CardContent
              sx={{
                p: 3,
                pt: summaryCollapsed ? 2 : 3,
                pb: summaryCollapsed ? 1 : 2,
                '&:last-child': { pb: summaryCollapsed ? 1 : 2 },
              }}
            >
              <Stack spacing={1.5}>
                <Collapse in={!summaryCollapsed} timeout="auto" unmountOnExit>
                  <Fade in={!summaryCollapsed} timeout={200}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', letterSpacing: 0.3 }}>
                        Appointment Details
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setSummaryCollapsed((s) => !s)}
                        aria-label="Collapse appointment details"
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                    </Box>
                  </Fade>
                </Collapse>

                <Collapse in={!summaryCollapsed} timeout="auto" unmountOnExit>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {appointment.appointmentTitle}
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {employeePhotoUrl ? (
                        <Avatar
                          src={employeePhotoUrl}
                          alt={`${employee.firstName} ${employee.lastName}`}
                          sx={{ width: 36, height: 36 }}
                        >
                          {employee.firstName?.charAt(0).toUpperCase() ?? '?'}
                        </Avatar>
                      ) : (
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'action.hover' }}>
                          <Person sx={{ color: 'text.secondary' }} />
                        </Avatar>
                      )}
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {employee.firstName} {employee.lastName}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <DateRange sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {formatDate(appointment.appointmentDate)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <AccessTime sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {formatTimeRange(appointment.appointmentTime, appointment.appointmentDuration)}
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 0.5 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Subtotal
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        ${appointment.billingAmount.toFixed(2)}
                      </Typography>
                    </Stack>
                    {shouldApplyTip && tipAmount > 0 && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Tip
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          ${tipAmount.toFixed(2)}
                        </Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Total
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        ${totalWithTip.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Collapse>

                <Collapse in={summaryCollapsed} timeout="auto" unmountOnExit>
                  <Stack spacing={0.75}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }} noWrap>
                        {appointment.appointmentTitle}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                        {formatCompactDateTime()}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        ${totalWithTip.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
                      <IconButton
                        size="small"
                        onClick={() => setSummaryCollapsed(false)}
                        aria-label="Expand appointment details"
                      >
                        <KeyboardArrowDown fontSize="medium" />
                      </IconButton>
                    </Box>
                  </Stack>
                </Collapse>
              </Stack>
            </CardContent>
          </Card>

          <Stack spacing={3} sx={{ mt: 3 }}>
            <Card elevation={2} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Enter Your Info:
                  </Typography>
                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="First Name"
                        size="small"
                        autoComplete="off"
                        value={firstName}
                        onFocus={cancelScheduledCollapse}
                        onChange={(e) => {
                          maybeCollapseSummary();
                          setFirstName(e.target.value);
                        }}
                        onInput={maybeCollapseSummary}
                        onBlur={scheduleCollapse}
                        error={Boolean(errors.firstName)}
                        helperText={errors.firstName}
                        fullWidth
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Last Name"
                        size="small"
                        autoComplete="off"
                        value={lastName}
                        onFocus={cancelScheduledCollapse}
                        onChange={(e) => {
                          maybeCollapseSummary();
                          setLastName(e.target.value);
                        }}
                        onInput={maybeCollapseSummary}
                        onBlur={scheduleCollapse}
                        error={Boolean(errors.lastName)}
                        helperText={errors.lastName}
                        fullWidth
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <style>{`
                      .PhoneInputInput {
                        height: 40px !important;
                        padding: 10.5px 14px !important;
                        font-size: 16px !important;
                        border-radius: 8px !important;
                        border: 1px solid rgba(0, 0, 0, 0.23) !important;
                        box-sizing: border-box !important;
                        background: #fff !important;
                      }
                    `}</style>
                      <PhoneInput
                        defaultCountry="US"
                        international
                        value={phone}
                        onFocus={cancelScheduledCollapse}
                        onChange={(value) => {
                          maybeCollapseSummary();
                          setPhone(value || '');
                        }}
                        onBlur={scheduleCollapse}
                        className="PhoneInput"
                        inputProps={{ autoComplete: 'off' }}
                      />
                      {Boolean(errors.phone) && (
                        <>
                          <style>{`.PhoneInputInput { border: 1px solid #ff1744 !important; }`}</style>
                          <div style={{ color: '#ff1744', fontSize: '0.75rem', marginTop: 4, marginLeft: 14 }}>
                            {errors.phone}
                          </div>
                        </>
                      )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Email"
                        size="small"
                        type="email"
                        autoComplete="off"
                        value={email}
                        onFocus={cancelScheduledCollapse}
                        onChange={(e) => {
                          maybeCollapseSummary();
                          setEmail(e.target.value);
                        }}
                        onInput={maybeCollapseSummary}
                        onBlur={scheduleCollapse}
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        fullWidth
                      />
                    </Grid2>
                    <Grid2 size={12}>
                      <TextField
                        label="Notes (optional)"
                        size="small"
                        multiline
                        minRows={3}
                        autoComplete="off"
                        value={notes}
                        onFocus={cancelScheduledCollapse}
                        onChange={(event) => {
                          const nextNotes = event.target.value;
                          maybeCollapseSummary();
                          setNotes(nextNotes);
                          // UI-only refactor; booking handler unchanged
                          handleAppointment({ ...appointment, notes: nextNotes });
                        }}
                        onInput={maybeCollapseSummary}
                        onBlur={scheduleCollapse}
                        inputProps={{ maxLength: 160 }}
                        helperText={`${notes.length}/160`}
                        fullWidth
                      />
                    </Grid2>
                  </Grid2>

                  {/* Pay Now / Pay in person toggle button (only if allowed & not required) */}
                  {allowCard && !mustPayNow && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Button
                        variant={showCardSection ? 'outlined' : 'contained'}
                        color="secondary"
                        onClick={() => {
                          maybeCollapseSummary();
                          setShowCardSection((s) => !s);
                        }}
                        sx={{ px: 3, width: { xs: '100%', sm: '50%' } }}
                      >
                        {showCardSection ? 'Pay in person' : 'Pay now'}
                      </Button>
                    </Box>
                  )}

                  {
                    /* Payment Details — visible if:
                      - required & allowed (auto-open), or
                      - optional & user clicked "Pay now" */
                  }
                  <Collapse in={allowCard && showCardSection} unmountOnExit>
                    {allowTips && (
                      <Stack spacing={1.5} sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Add a tip
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          value={selectedTipOption}
                          onChange={(_, value) => {
                            if (!value) return;
                            setSelectedTipOption(value);
                          }}
                          size="small"
                          sx={{
                            width: '100%',
                            display: 'flex',
                            flexWrap: 'nowrap',
                            gap: 0,
                            '& .MuiToggleButton-root': {
                              flex: 1,
                              minWidth: 0,
                            },
                          }}
                        >
                          <ToggleButton
                            value="0"
                            sx={{
                              textTransform: 'none',
                              py: 1.1,
                              '& .tip-subtext': { color: 'text.secondary' },
                              '&.Mui-selected .tip-subtext': { color: 'inherit', opacity: 0.75 },
                            }}
                          >
                            <Stack spacing={0} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                No tip
                              </Typography>
                              <Typography variant="caption" className="tip-subtext" sx={{ lineHeight: 1.1 }}>
                                {formatUsd(0)}
                              </Typography>
                            </Stack>
                          </ToggleButton>
                          <ToggleButton
                            value="15"
                            sx={{
                              textTransform: 'none',
                              py: 1.1,
                              '& .tip-subtext': { color: 'text.secondary' },
                              '&.Mui-selected .tip-subtext': { color: 'inherit', opacity: 0.75 },
                            }}
                          >
                            <Stack spacing={0} alignItems="center">
                              <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                15%
                              </Typography>
                              <Typography variant="caption" className="tip-subtext" sx={{ lineHeight: 1.1 }}>
                                {formatUsd(tip15Amount)}
                              </Typography>
                            </Stack>
                          </ToggleButton>
                          <ToggleButton
                            value="20"
                            sx={{
                              textTransform: 'none',
                              py: 1.1,
                              '& .tip-subtext': { color: 'text.secondary' },
                              '&.Mui-selected .tip-subtext': { color: 'inherit', opacity: 0.75 },
                            }}
                          >
                            <Stack spacing={0} alignItems="center">
                              <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                20%
                              </Typography>
                              <Typography variant="caption" className="tip-subtext" sx={{ lineHeight: 1.1 }}>
                                {formatUsd(tip20Amount)}
                              </Typography>
                            </Stack>
                          </ToggleButton>
                          <ToggleButton
                            value="25"
                            sx={{
                              textTransform: 'none',
                              py: 1.1,
                              '& .tip-subtext': { color: 'text.secondary' },
                              '&.Mui-selected .tip-subtext': { color: 'inherit', opacity: 0.75 },
                            }}
                          >
                            <Stack spacing={0} alignItems="center">
                              <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                25%
                              </Typography>
                              <Typography variant="caption" className="tip-subtext" sx={{ lineHeight: 1.1 }}>
                                {formatUsd(tip25Amount)}
                              </Typography>
                            </Stack>
                          </ToggleButton>
                          <ToggleButton
                            value="custom"
                            sx={{
                              textTransform: 'none',
                              py: 1.1,
                              '& .tip-subtext': { color: 'text.secondary' },
                              '&.Mui-selected .tip-subtext': { color: 'inherit', opacity: 0.75 },
                            }}
                          >
                            <Stack spacing={0} alignItems="center">
                              <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                Custom
                              </Typography>
                              <Typography variant="caption" className="tip-subtext" sx={{ lineHeight: 1.1 }}>
                                {formatUsd(customTipDisplayAmount)}
                              </Typography>
                            </Stack>
                          </ToggleButton>
                        </ToggleButtonGroup>
                        {selectedTipOption === 'custom' && (
                          <TextField
                            label="Custom tip amount"
                            type="number"
                            size="small"
                            value={customTipInput}
                            onChange={(event) => setCustomTipInput(event.target.value)}
                            inputProps={{ min: 0, step: '0.01' }}
                            error={isTipInputInvalid}
                            helperText={
                              isCustomTipInvalid
                                ? 'Enter a valid non-negative tip amount.'
                                : isCustomTipTooHigh
                                  ? 'Custom tip max is $1000.00.'
                                  : 'Enter amount in USD.'
                            }
                            sx={{ maxWidth: 240 }}
                          />
                        )}
                        <Divider sx={{ my: 0.5 }} />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Subtotal
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {formatUsd(appointment.billingAmount)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Tip
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {formatUsd(tipAmount)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Total
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {formatUsd(totalWithTip)}
                          </Typography>
                        </Stack>
                      </Stack>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Enter Your Card Details:
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        mt: 1,
                        backgroundColor: 'action.hover',
                      }}
                    >
                      <CardElement
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#424770',
                              '::placeholder': { color: '#aab7c4' },
                            },
                            invalid: { color: '#9e2146' },
                          },
                        }}
                        onChange={(e) => setCardError(e.error ? e.error.message : null)}
                      />
                    </Box>
                    {cardError && (
                      <Typography sx={{ mt: 1, color: 'error.main' }}>
                        {cardError}
                      </Typography>
                    )}
                  </Collapse>
                </Stack>
              </CardContent>
            </Card>

            {/* Optional customer SMS checkbox */}
            {enabledCustomerSMS && (
              <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, backgroundColor: 'background.paper' }}>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendCustomerSMS}
                        onChange={(e) => setSendCustomerSMS(e.target.checked)}
                        size="small"
                        color="primary"
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>(Optional) Text me updates</Typography>}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    By checking the box above, and entering your phone number, you agree to receive appointment reminder
                    texts from {businessName}. Texts are only sent to US/Canada numbers. Message + data rates apply.
                    View terms/conditions and privacy policy here.
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Box>

        {/* Sticky footer */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            py: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'background.paper',
            zIndex: 2,
          }}
        >
          <Box sx={{ width: '50%', display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="text"
              onClick={goToPreviousStep}
              sx={{ width: '100%', borderRadius: '4px', fontWeight: 600, fontSize: '1rem', py: 1.2 }}
            >
              Back
            </Button>
          </Box>
          <Box sx={{ width: '50%', display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleConfirmAppointment}
              disabled={confirmAppointmentMutation.isPending || isTipInputInvalid}
              sx={{ width: '100%', borderRadius: '4px', fontWeight: 600, fontSize: '1rem', py: 1.2 }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Container>
    </Grid2>
  );
};

export default ConfirmAppointment;
